import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch("https://readdy.ai/api/form/d7ejum3fimgqccpmabng", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubscribed(true);
    } catch {
      setSubscribed(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-amber-950 text-white rounded-t-3xl">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b8685d651cf0232fd706d280f6405113.jpeg" alt="Logo" className="w-10 h-10 rounded-full border-2 border-amber-400 object-cover" />
              <span className="font-bold text-amber-400" style={{ fontFamily: "'Playfair Display', serif" }}>SuperMiddleEast</span>
            </div>
            <p className="text-amber-200 text-sm mb-5 leading-relaxed">{t("footer.tagline")}</p>
            <p className="text-amber-400 text-xs font-semibold uppercase tracking-wider mb-3">{t("footer.stayUpdated")}</p>
            {subscribed ? (
              <p className="text-amber-300 text-sm">{t("footer.thankYou")}</p>
            ) : (
              <form data-readdy-form onSubmit={handleSubscribe} className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("footer.emailPlaceholder")}
                  className="flex-1 bg-amber-900 border border-amber-700 text-white placeholder-amber-500 text-sm px-3 py-2 rounded-md focus:outline-none focus:border-amber-400"
                  required
                />
                <button type="submit" disabled={submitting} className="bg-amber-500 hover:bg-amber-400 text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer">
                  {submitting ? "..." : t("footer.join")}
                </button>
              </form>
            )}
          </div>

          <div>
            <p className="text-amber-400 text-xs font-semibold uppercase tracking-wider mb-4">{t("footer.marketplace")}</p>
            <ul className="space-y-2">
              {[
                { key: "footer.findSuppliers", path: "/products" },
                { key: "footer.browseProducts", path: "/products" },
                { key: "footer.dropshippingHub", path: "/products?dropshipping=true" },
                { key: "footer.verifiedFactories", path: "/dubai" },
                { key: "footer.countryPages", path: "/dubai" },
              ].map((item) => (
                <li key={item.key}>
                  <Link to={item.path} className="text-amber-200 text-sm hover:text-amber-400 transition-colors">{t(item.key)}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-amber-400 text-xs font-semibold uppercase tracking-wider mb-4">{t("footer.forSuppliers")}</p>
            <ul className="space-y-2">
              {[
                { key: "footer.registerSupplier", path: "/register-supplier" },
                { key: "footer.verificationProcess", path: "/how-it-works" },
                { key: "footer.pricingPlans", path: "/register-factory" },
                { key: "footer.influencerProgram", path: "/influencer-program" },
                { key: "footer.sellerResources", path: "/how-it-works" },
              ].map((item) => (
                <li key={item.key}>
                  <Link to={item.path} className="text-amber-200 text-sm hover:text-amber-400 transition-colors">{t(item.key)}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-amber-400 text-xs font-semibold uppercase tracking-wider mb-4">{t("footer.contactUs")}</p>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-line text-amber-400 text-sm"></i></div>
                <span className="text-amber-200 text-sm">+971 4 000 0000</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-mail-line text-amber-400 text-sm"></i></div>
                <a href="mailto:info@supermiddleeast.com" className="text-amber-200 text-sm hover:text-amber-400">info@supermiddleeast.com</a>
              </div>
              <div className="mt-4">
                <p className="text-amber-400 text-xs font-semibold uppercase tracking-wider mb-2">{t("footer.headquarters")}</p>
                <p className="text-amber-200 text-sm leading-relaxed">Dubai World Trade Centre<br />Sheikh Zayed Road, Dubai, UAE</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-amber-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {[
              { icon: "ri-facebook-fill", label: "Facebook" },
              { icon: "ri-youtube-fill", label: "YouTube" },
              { icon: "ri-twitter-x-fill", label: "Twitter" },
              { icon: "ri-instagram-line", label: "Instagram" },
              { icon: "ri-tiktok-fill", label: "TikTok" },
            ].map((s) => (
              <a key={s.label} href="#" aria-label={s.label} className="w-8 h-8 flex items-center justify-center text-amber-400 hover:text-amber-300 transition-colors cursor-pointer">
                <i className={`${s.icon} text-lg`}></i>
              </a>
            ))}
          </div>
          <p className="text-amber-500 text-xs text-center">{t("footer.rights")}</p>
          <div className="flex gap-4">
            <a href="#" className="text-amber-400 text-xs hover:text-amber-300 transition-colors">{t("footer.privacy")}</a>
            <a href="#" className="text-amber-400 text-xs hover:text-amber-300 transition-colors">{t("footer.terms")}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
